/**
 *
 * This is V2 meeting mode
 *
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

// Types
import { WorkspaceMeetingModeState } from 'Shared/types/WorkspaceMeetingMode.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useCurrentlyTypingParticipants } from './hooks/useCurrentlyTypingParticpants';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useUserFlag } from 'Shared/utils/hooks/use-user-flag';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';
import WorkspaceMeetingModeConstants from 'Shared/constants/WorkspaceMeetingMode.constants';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { sortOnPosition } from 'Shared/utils/MeetingItem.utils';
import {
  isEmpty,
  createRichTextEditorState,
  editorStateToJsonString,
} from 'Wpds/components/rich-text/utils/EditorState.utils';
import { convertMomentToUnix } from 'Shared/utils/DateTime.utils';
import { updateEntitysOfGifBeforeSend } from 'Shared/components/DraftEditor/plugins/addGifPlugin';

// Selectors
import * as WorkspaceMeetingModeSelectors from 'Shared/selectors/WorkspaceMeetingMode.selectors';
import * as MeetingItemSelectors from 'Shared/selectors/MeetingItems.selectors';

// Actions
import * as WorkspaceMeetingModeActions from 'Shared/actions/WorkspaceMeetingMode.actions';
import * as meetingItemActions from 'Shared/actions/MeetingItems.actions';
import * as workspaceItemCommentActions from 'Shared/actions/WorkspaceItemComments.actions';

// Helpers
import { toggleIntercom } from 'Shared/helpers';
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Components
import { WpButton } from 'Wpds/components/button';
import { WpPageLayout, WpPageBody, WpPageHeader } from 'Wpds/components/page-layout';
import { WpModal, WpModalHeader, WpModalBody } from 'Wpds/components/modal';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';
import WpIcon from 'Wpds/components/icon/WpIcon';
import AgendaPanel from './partials/AgendaPanel/AgendaPanel';
import TopicPanel from './partials/TopicPanel/TopicPanel';
import MissingTopicPanel from './partials/MissingTopicPanel/MissingTopicPanel';
import NotesPanel from './partials/NotesPanel/NotesPanel';
import AddNote from './partials/AddNote/AddNote';
import AddActionItem from './partials/AddActionItem/AddActionItem';
import EmptyWorkspaceCard from './partials/EmptyWorkspaceCard/EmptyWorkspaceCard';
import NextSteps from './partials/NextSteps/NextSteps';
import TopicLoading from './partials/TopicLoading/TopicLoading';
import MeetingModeIntroModal from './partials/MeetingModeIntroModal/MeetingModeIntroModal';
import MeetingModeEnding from './partials/MeetingModeEnding/MeetingModeEnding';
import SomeoneIsTypingModal from './partials/SomeoneIsTypingModal/SomeoneIsTypingModal';
import MeetingAttendees from './partials/MeetingAttendees/MeetingAttendees';
import NavigationWarningModal from './partials/NavigationWarningModal/NavigationWarningModal';

// Styles
import './MeetingMode.scss';
import AgendaList from './partials/AgendaList/AgendaList';

const MeetingMode = () => {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();
  const history = useHistory();
  const { isZoom } = useApp();

  // const [hasSeenNavigationWarning] = useState(useUserFlag({ flag: 'has_seen_meeting_mode_navigation_warning' }));
  const [noteEditorState, setNoteEditorState] = useState(createRichTextEditorState(''));
  const [isLeaving, toggleIsLeaving] = useToggle(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMeetingItemsLoaded, toggleIsMeetingItemsLoaded] = useToggle(false);
  const [isShowingEnding, toggleIsShowingEnding] = useToggle(false);
  const [isSessionLoaded, toggleIsSessionLoaded] = useToggle(false);
  const [isAgendaModalOpen, toggleIsAgendaModalOpen] = useToggle(false);
  const [isSomeoneTypingModalOpen, toggleIsSomeoneTypingModalOpen] = useToggle(false);
  const [isNavigationWarningModalOpen, toggleIsNavigationWarningModalOpen] = useToggle(false);
  const [navigationOnHoldSettings, setNavigationOnHoldSettings] = useState<{
    itemUuid?: string;
    direction?: 'next' | 'back';
  }>({});

  const [isShowingEmptyStateMessage, toggleIsShowingEmptyStateMessage] = useToggle(false);
  const hasSeenNavigationWarning = useUserFlag({ flag: 'has_seen_meeting_mode_navigation_warning' });

  const workspaceDiscussionTopics = useSelector(state =>
    MeetingItemSelectors.getWorkspaceDiscussionTopics(state, { worksapceUuid: workspaceId })
  );

  const activeMeetingMode = useSelector(state =>
    WorkspaceMeetingModeSelectors.getWorkspaceMeetingModeItemByWorkspaceSlug(state, { workspaceSlug: workspaceId })
  );

  const typingParticipants = useCurrentlyTypingParticipants(activeMeetingMode);
  const { isMedium, isXtraLarge } = useResponsiveScreenWidth({
    isAtLeast: true,
  });
  const showAgenda = isMedium;

  const state = WorkspaceMeetingModeHelper.getState(activeMeetingMode);

  const meetingModeSessionId = WorkspaceMeetingModeHelper.getId(activeMeetingMode);

  const isTraversingitems = state === WorkspaceMeetingModeState.WORKSPACE_ITEM_TRAVERSAL;
  const isNextSteps = state === WorkspaceMeetingModeState.NEXT_STEPS;

  const meetingItemUuid = WorkspaceMeetingModeHelper.getWorkspaceMeetingItemUuid(activeMeetingMode);
  const meetingItemId = WorkspaceMeetingModeHelper.getWorkspaceMeetingItemId(activeMeetingMode);

  const meetingModeSessionCreatedAtUnix = activeMeetingMode
    ? convertMomentToUnix(WorkspaceMeetingModeHelper.getCreatedAt(activeMeetingMode))
    : null;

  const meetingItemOrderedKeys = (() => {
    const filterdItems = workspaceDiscussionTopics
      .filter(item => {
        const itemCompletedAt = MeetingItemHelpers.getCompletedAt(item);
        return !itemCompletedAt || itemCompletedAt >= meetingModeSessionCreatedAtUnix;
      })
      .filter(item => {
        return !isEmpty(MeetingItemHelpers.getBody(item));
      })
      .filter(item => {
        if (MeetingItemHelpers.getIsSection(item)) {
          const uuid = MeetingItemHelpers.getUuid(item);
          const children = workspaceDiscussionTopics.filter(
            item => MeetingItemHelpers.getParentUuid(item) === uuid && !isEmpty(MeetingItemHelpers.getBody(item))
          );
          return children.size > 0;
        }
        return true;
      });

    const itemsSortedByPosition = sortOnPosition({
      items: filterdItems,
      sortKey: 'workspace_break_position_ties_by_updated_at',
    })
      .valueSeq()
      .toJS();
    return itemsSortedByPosition
      .filter(({ parentId }) => !parentId)
      .map(item => [item, ...itemsSortedByPosition.filter(({ parentId }) => parentId === item.id)])
      .flat()
      .map(({ uuid }) => uuid);
  })();

  const handleSaveDraftNote = useCallback(() => {
    // if the current user typed a note without clicking the save button,
    // auto save for them so they don't loose it
    if (!isEmpty(noteEditorState)) {
      const payload = {
        body: editorStateToJsonString(updateEntitysOfGifBeforeSend(noteEditorState)),
        meetingModeSessionId,
      };

      dispatch(workspaceItemCommentActions.createWorkspaceItemComment(meetingItemId, payload)).then(() =>
        setNoteEditorState(createRichTextEditorState(''))
      );
    }
  }, [noteEditorState]);

  const handleLeaveButtonClick = useCallback(() => {
    toggleIsLeaving();
    handleSaveDraftNote();
    dispatch(WorkspaceMeetingModeActions.leaveMeetingMode(workspaceId)).then(() => {
      history.push(PageUrlsUtils.makeWorkspaceUrl(workspaceId));
    });
  }, [history, workspaceId, toggleIsLeaving, handleSaveDraftNote]);

  const handleNavigateToNextSteps = () => {
    const params = {
      [WorkspaceMeetingModeConstants.Params.STATE]: WorkspaceMeetingModeState.NEXT_STEPS,
    };

    dispatch(WorkspaceMeetingModeActions.updateMeetingModeV2(workspaceId, params));
  };

  const handleNavigateToItem = (itemUuid, direction: 'next' | 'back' = 'next', onFailCallback?) => {
    if (typingParticipants.size > 0) {
      toggleIsSomeoneTypingModalOpen();
      setNavigationOnHoldSettings({ itemUuid, direction });
      if (onFailCallback) onFailCallback();
      return false;
    }
    if (!hasSeenNavigationWarning) {
      toggleIsNavigationWarningModalOpen();
      setNavigationOnHoldSettings({ itemUuid, direction });
      if (onFailCallback) onFailCallback();
      return false;
    }
    setIsLoading(true);
    handleSaveDraftNote();

    if (isAgendaModalOpen) toggleIsAgendaModalOpen();
    if (itemUuid === 'wrapup') {
      handleNavigateToNextSteps();
      return;
    }

    const item = workspaceDiscussionTopics.get(itemUuid);
    const isSection = MeetingItemHelpers.getIsSection(item);

    const hasChildren =
      workspaceDiscussionTopics.filter(
        item =>
          !MeetingItemHelpers.getCompleted(item) &&
          MeetingItemHelpers.getParentUuid(item) === itemUuid &&
          !isEmpty(MeetingItemHelpers.getBody(item))
      ).size > 0;

    let navigateToUuid = itemUuid;
    if (isSection || hasChildren) {
      if (direction === 'back') navigateToUuid = meetingItemOrderedKeys[meetingItemOrderedKeys.indexOf(itemUuid) - 1];
      else navigateToUuid = meetingItemOrderedKeys[meetingItemOrderedKeys.indexOf(itemUuid) + 1];
    }

    const params = {
      [WorkspaceMeetingModeConstants.Params.ITEM_UUID]: navigateToUuid,
      [WorkspaceMeetingModeConstants.Params.STATE]: WorkspaceMeetingModeState.WORKSPACE_ITEM_TRAVERSAL,
    };

    dispatch(WorkspaceMeetingModeActions.updateMeetingModeV2(workspaceId, params)).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    // need a slight delay to handle when the page refreshes
    setTimeout(() => toggleIntercom({ show: false }), 1000);

    return function cleanup() {
      toggleIntercom({ show: true });
    };
  }, []);

  useEffect(() => {
    dispatch(WorkspaceMeetingModeActions.startMeetingMode(workspaceId)).then(response => {
      if (response === 'Add a discussion topic to this workspace to start meeting mode.') {
        toggleIsShowingEmptyStateMessage();
      } else toggleIsSessionLoaded();
    });
  }, [workspaceId]);

  useEffect(() => {
    // if the last item in worksapce was deleted or moved to a new workspace,
    // show empty state message message to get user back into workspace
    if (isSessionLoaded && isMeetingItemsLoaded && workspaceDiscussionTopics.size === 0) {
      toggleIsShowingEmptyStateMessage();
    }
  }, [isSessionLoaded, isMeetingItemsLoaded, workspaceDiscussionTopics]);

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 300);
  }, [meetingItemUuid]);

  useEffect(() => {
    if (!meetingModeSessionCreatedAtUnix) return;
    const params = {
      [QueryParamConstants.Api.TYPE]: QueryParamConstants.Api.DISCUSSION_TOPICS,
      [QueryParamConstants.Api.WORKSPACE_ID]: workspaceId,
      [QueryParamConstants.Api.FILTER_BY]: QueryParamConstants.Api.COMPLETED_SINCE_OR_UNCOMPLETED,
      [QueryParamConstants.Api.COMPLETED_SINCE]: meetingModeSessionCreatedAtUnix,
      [QueryParamConstants.Api.DATA_FORMAT]: QueryParamConstants.Api.JSONAPI,
    };

    dispatch(meetingItemActions.fetchMeetingItems('workspace', params)).then(() => {
      toggleIsMeetingItemsLoaded();
      setIsLoading(false);
    });
  }, [workspaceId, meetingModeSessionCreatedAtUnix]);

  useEffect(() => {
    if (isSomeoneTypingModalOpen && typingParticipants.size === 0) {
      toggleIsSomeoneTypingModalOpen();
      handleNavigateToItem(navigationOnHoldSettings.itemUuid, navigationOnHoldSettings.direction);
      setNavigationOnHoldSettings({});
    }
  }, [isSomeoneTypingModalOpen, typingParticipants, navigationOnHoldSettings]);

  useEffect(() => {
    // this handles logic for displaying the meeting adjourned ending screen
    // this logic assumes that an active session was loaded when we loaded meeting page (isSessionLoaded) and that now
    // that active session is undefined. (meeting adjourned)
    if (isSessionLoaded && activeMeetingMode == undefined) toggleIsShowingEnding();
  }, [activeMeetingMode, isSessionLoaded, isZoom]);

  const showMeetingMode =
    !isShowingEmptyStateMessage && Boolean(activeMeetingMode) && isMeetingItemsLoaded && !isShowingEnding;

  const meetingItem = useSelector(state => MeetingItemSelectors.getMeetingItemByUuid(state, { meetingItemUuid })).get(
    meetingItemUuid
  );
  const meetingItemPresent = Boolean(meetingItem);

  const baseClassName = 'MeetingMode';

  const loading = (
    <>
      <div className={`${baseClassName}__body__topic`}>
        <TopicLoading />
      </div>
      {isXtraLarge && <div className={`${baseClassName}__body__notes`}></div>}
    </>
  );

  if (isShowingEnding) return <MeetingModeEnding />;

  return (
    <div className={baseClassName}>
      {showAgenda && showMeetingMode && (
        <div className={`${baseClassName}__agenda`}>
          <AgendaPanel
            meetingModeSession={activeMeetingMode}
            meetingItemOrderedKeys={meetingItemOrderedKeys}
            handleNavigateToItem={handleNavigateToItem}
          />
        </div>
      )}
      <WpPageLayout className={`${baseClassName}__main`} width="container">
        <WpPageHeader sticky>
          <div className={`${baseClassName}__header`}>
            {showAgenda ? (
              <h5 className={`${baseClassName}__header__title`}>Meeting Mode</h5>
            ) : (
              <>
                {showMeetingMode && (
                  <div className={`${baseClassName}__header__action-area`}>
                    <WpTooltip>
                      <WpTooltipTrigger>
                        <WpButton size="lg" variant="tertiary" onClick={toggleIsAgendaModalOpen}>
                          <WpIcon name="list" />
                        </WpButton>
                      </WpTooltipTrigger>
                      <WpTooltipContent>View Agenda</WpTooltipContent>
                    </WpTooltip>
                    {showMeetingMode && <MeetingAttendees meetingModeSession={activeMeetingMode} showLabel={false} />}
                  </div>
                )}
              </>
            )}

            {showMeetingMode && (
              // if on the wrap up screen, we visually hide the exit button,
              // this maintains the height of the header to match the agenda header
              <div
                className={`${baseClassName}__header__exit-btn 
                ${isNextSteps ? `${baseClassName}__header__exit-btn--hidden` : ''}`}
              >
                <WpButton variant="tertiary" onClick={handleLeaveButtonClick} loading={isLeaving}>
                  Exit Meeting Mode
                </WpButton>
              </div>
            )}
          </div>
        </WpPageHeader>
        {isShowingEmptyStateMessage && <EmptyWorkspaceCard />}
        {!showMeetingMode && !isShowingEmptyStateMessage && (
          <WpPageBody>
            <div className={`${baseClassName}__body`}>{loading}</div>
          </WpPageBody>
        )}
        {showMeetingMode && (
          <WpPageBody>
            {isTraversingitems && (
              <div className={`${baseClassName}__body`}>
                {isLoading ? (
                  <>{loading}</>
                ) : (
                  <>
                    {meetingItemPresent && (
                      <>
                        <div className={`${baseClassName}__body__topic`}>
                          <TopicPanel
                            meetingModeSession={activeMeetingMode}
                            meetingItemOrderedKeys={meetingItemOrderedKeys}
                            handleNavigateToItem={handleNavigateToItem}
                          />
                          <div className={`${baseClassName}__body__topic__add-note`}>
                            <AddNote meetingModeSession={activeMeetingMode} onChange={setNoteEditorState} />
                          </div>
                          <div className={`${baseClassName}__body__topic__add-action-item`}>
                            <AddActionItem meetingModeSession={activeMeetingMode} />
                          </div>
                        </div>
                        {isXtraLarge && (
                          <div className={`${baseClassName}__body__notes`}>
                            <NotesPanel meetingModeSession={activeMeetingMode} />
                          </div>
                        )}
                      </>
                    )}
                    {!meetingItemPresent && (
                      <>
                        <div className={`${baseClassName}__body__topic`}>
                          <MissingTopicPanel
                            workspaceId={workspaceId}
                            meetingModeSession={activeMeetingMode}
                            meetingItemOrderedKeys={meetingItemOrderedKeys}
                            handleNavigateToItem={handleNavigateToItem}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {isNextSteps && (
              <div className={`${baseClassName}__body`}>
                <div className={`${baseClassName}__body__topic`}>
                  <NextSteps
                    meetingModeSession={activeMeetingMode}
                    meetingItemOrderedKeys={meetingItemOrderedKeys}
                    handleNavigateToItem={handleNavigateToItem}
                  />
                </div>
                {isXtraLarge && <div className={`${baseClassName}__body__notes`}></div>}
              </div>
            )}
          </WpPageBody>
        )}
      </WpPageLayout>
      <MeetingModeIntroModal />
      {isSomeoneTypingModalOpen && (
        <SomeoneIsTypingModal
          typingParticipants={typingParticipants}
          isOpen={isSomeoneTypingModalOpen}
          toggleIsOpen={toggleIsSomeoneTypingModalOpen}
        />
      )}
      {isAgendaModalOpen && (
        <WpModal isOpen={isAgendaModalOpen} onToggle={toggleIsAgendaModalOpen} size="sm">
          <WpModalHeader>Agenda</WpModalHeader>
          <WpModalBody>
            <AgendaList
              meetingItemOrderedKeys={meetingItemOrderedKeys}
              meetingModeSession={activeMeetingMode}
              handleNavigateToItem={handleNavigateToItem}
              showLabel={false}
            />
          </WpModalBody>
        </WpModal>
      )}
      {isNavigationWarningModalOpen && (
        <NavigationWarningModal
          isOpen={isNavigationWarningModalOpen}
          toggleIsOpen={toggleIsNavigationWarningModalOpen}
          onContinueClick={() => {
            handleNavigateToItem(navigationOnHoldSettings.itemUuid, navigationOnHoldSettings.direction);
            setNavigationOnHoldSettings({});
          }}
          onCancelClick={() => {
            setNavigationOnHoldSettings({});
          }}
        />
      )}
    </div>
  );
};

export default MeetingMode;
